import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronDown, X } from 'lucide-react';

/**
 * Date range filter component
 * Allows selecting start and end dates
 */
const DateRangeFilter = ({
  label,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  presets = true, // Show preset options
  className = '',
  disabled = false,
  testId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const clearDates = (e) => {
    e.stopPropagation();
    onStartDateChange('');
    onEndDateChange('');
  };

  const applyPreset = (preset) => {
    const today = new Date();
    let start, end;

    switch (preset) {
      case 'today':
        start = end = today.toISOString().split('T')[0];
        break;
      case 'this_week':
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay());
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        start = startOfWeek.toISOString().split('T')[0];
        end = endOfWeek.toISOString().split('T')[0];
        break;
      case 'this_month':
        start = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
        break;
      case 'next_7_days':
        start = today.toISOString().split('T')[0];
        const next7 = new Date(today);
        next7.setDate(today.getDate() + 7);
        end = next7.toISOString().split('T')[0];
        break;
      case 'next_30_days':
        start = today.toISOString().split('T')[0];
        const next30 = new Date(today);
        next30.setDate(today.getDate() + 30);
        end = next30.toISOString().split('T')[0];
        break;
      case 'overdue':
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        start = '2020-01-01';
        end = yesterday.toISOString().split('T')[0];
        break;
      default:
        return;
    }

    onStartDateChange(start);
    onEndDateChange(end);
    setIsOpen(false);
  };

  const getDisplayText = () => {
    if (!startDate && !endDate) return 'Any date';
    if (startDate && endDate) {
      if (startDate === endDate) return startDate;
      return `${startDate} - ${endDate}`;
    }
    if (startDate) return `From ${startDate}`;
    if (endDate) return `Until ${endDate}`;
    return 'Any date';
  };

  const hasValue = startDate || endDate;

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {label && (
        <label className="block text-xs font-medium text-[var(--clr-text-muted)] mb-1 flex items-center gap-1">
          <Calendar className="w-3 h-3" />
          {label}
        </label>
      )}
      
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        data-testid={testId}
        className={`
          w-full px-3 py-2 text-sm text-left border rounded-lg flex items-center justify-between gap-2
          ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white hover:border-[var(--clr-brand-primary)] cursor-pointer'}
          ${isOpen ? 'ring-2 ring-[var(--clr-brand-primary)] border-[var(--clr-brand-primary)]' : 'border-gray-300'}
          ${hasValue ? 'text-[var(--clr-text-primary)]' : 'text-[var(--clr-text-muted)]'}
        `}
      >
        <span className="truncate flex-1">{getDisplayText()}</span>
        <div className="flex items-center gap-1">
          {hasValue && (
            <span 
              onClick={clearDates}
              className="p-0.5 hover:bg-gray-200 rounded"
            >
              <X className="w-3 h-3" />
            </span>
          )}
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
          {/* Date Inputs */}
          <div className="p-3 space-y-3">
            <div>
              <label className="block text-xs text-[var(--clr-text-muted)] mb-1">Start Date</label>
              <input
                type="date"
                value={startDate || ''}
                onChange={(e) => onStartDateChange(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-[var(--clr-brand-primary)]"
              />
            </div>
            <div>
              <label className="block text-xs text-[var(--clr-text-muted)] mb-1">End Date</label>
              <input
                type="date"
                value={endDate || ''}
                onChange={(e) => onEndDateChange(e.target.value)}
                className="w-full px-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-[var(--clr-brand-primary)]"
              />
            </div>
          </div>

          {/* Presets */}
          {presets && (
            <div className="border-t border-gray-100 p-2 bg-gray-50">
              <div className="text-xs text-[var(--clr-text-muted)] mb-2 px-1">Quick Select</div>
              <div className="grid grid-cols-2 gap-1">
                {[
                  { id: 'today', label: 'Today' },
                  { id: 'this_week', label: 'This Week' },
                  { id: 'this_month', label: 'This Month' },
                  { id: 'next_7_days', label: 'Next 7 Days' },
                  { id: 'next_30_days', label: 'Next 30 Days' },
                  { id: 'overdue', label: 'Overdue' },
                ].map(preset => (
                  <button
                    key={preset.id}
                    onClick={() => applyPreset(preset.id)}
                    className="px-2 py-1 text-xs text-left rounded hover:bg-gray-200 transition-colors"
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="border-t border-gray-100 p-2 flex justify-between">
            <button
              onClick={clearDates}
              className="px-2 py-1 text-xs text-[var(--clr-text-muted)] hover:text-[var(--clr-sev-blocked)]"
            >
              Clear
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="px-3 py-1 text-xs bg-[var(--clr-brand-primary)] text-white rounded hover:bg-opacity-90"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;
